import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Wallet, Plus, Search, Loader2, ChevronLeft,
  Play, Package, IndianRupee, Settings2, Phone,
  CheckCircle2, AlertTriangle, Monitor, TrendingUp, RotateCcw,
  Trash2, Clock, CalendarClock, ListFilter, Users,
  ArrowUpRight, ArrowDownRight, CreditCard, Info, XCircle, KeyRound, History, MapPin
} from 'lucide-react';
import CustomerSwitcher, { useActiveCustomer } from '@/components/CustomerSwitcher';
import IPTVDistributorsPanel from './IPTVDistributorsPanel';

const API = process.env.REACT_APP_BACKEND_URL;

const getError = (data, fallback) => {
  if (!data) return fallback;
  if (typeof data.detail === 'string') return data.detail;
  if (Array.isArray(data.detail)) return data.detail.map(d => d.msg || d.message).filter(Boolean).join(', ') || fallback;
  if (data.message) return data.message;
  return fallback;
};

export default function PartnerIPTV() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [pendingRecharge, setPendingRecharge] = useState(location.state?.recharge || null);
  const [switchPackageId, setSwitchPackageId] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState({});
  const [packages, setPackages] = useState([]);
  const [states, setStates] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [subSearch, setSubSearch] = useState('');
  const [subFilter, setSubFilter] = useState('all'); // all | active | expiring | expired
  const [lcoSearch, setLcoSearch] = useState('');
  const [lcos, setLcos] = useState([]);

  // IPTV Wallet
  const [iptvBalance, setIptvBalance] = useState(0);
  const [iptvTransactions, setIptvTransactions] = useState([]);
  const [showTopUp, setShowTopUp] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpLoading, setTopUpLoading] = useState(false);

  // Dialogs
  const [showSubscribe, setShowSubscribe] = useState(null);
  const [showRenewDialog, setShowRenewDialog] = useState(null);
  const [showPreRenewDialog, setShowPreRenewDialog] = useState(null);
  const [showLookup, setShowLookup] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showSetPricing, setShowSetPricing] = useState(null);
  const [showCreditLCO, setShowCreditLCO] = useState(null);
  const [lookupResult, setLookupResult] = useState(null);
  const [statusDialog, setStatusDialog] = useState(null); // { loading, data, sub }
  const [renewStatus, setRenewStatus] = useState(null); // { loading, data }

  // Forms
  const [subForm, setSubForm] = useState({ mobile_no: '', subscriber_name: '', state: '', city: '', address: '', pincode: '', lco_customer_id: '' });
  const [lookupPhone, setLookupPhone] = useState('');
  const [deletePhone, setDeletePhone] = useState('');
  const [pricingForm, setPricingForm] = useState({ lco_price: '' });
  const [creditAmount, setCreditAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // LCO detection — when an LCO uses this page, partner-only endpoints (lco list,
  // lco pricing, partner wallet) are skipped, and per-customer reads go through
  // the query-param endpoints (/iptv/portal/balance|transactions|subscriptions).
  const [isLco, setIsLco] = useState(false);
  const [isDistributor, setIsDistributor] = useState(false);
  // Partner viewers can no longer activate customers or renew without an
  // LCO/Distributor assigned. We surface a helpful dialog instead of a
  // generic error toast.
  const [showAssignLcoDialog, setShowAssignLcoDialog] = useState(null); // { title, message, subscription? }
  const isPartner = !isLco && !isDistributor;
  // POLICY (Sep 2026): only IPTV Distributor callers must route through an
  // LCO. Regular Partners are allowed to renew direct subs — so the amber
  // "Needs LCO" chip / renew-blocker banner only fires when the caller is
  // an IPTV Distributor viewing a sub that isn't assigned to one of their
  // LCOs.
  const needsLcoAssignment = (sub) =>
    isDistributor && (
      !sub?.lco_customer_id
      || sub.lco_customer_id === sub.customer_id
      || sub.lco_customer_id === sub.partner_id
    );
  // When we detect Distributor role after portal-init, default to the
  // Distributor's dedicated tab.
  useEffect(() => { if (isDistributor) setTab('my-distributor'); }, [isDistributor]);
  const [lcoCustomerId, setLcoCustomerId] = useState(null);
  const [allCustomers, setAllCustomers] = useState([]);
  const { activeId, setActiveId } = useActiveCustomer(allCustomers);

  const basePath = window.location.pathname.startsWith('/partner') ? '/partner' : '/portal';
  const token = localStorage.getItem('portal_token');
  const authHeader = { 'Authorization': `Bearer ${token}` };

  // Handle payment callback URL params
  useEffect(() => {
    const payment = searchParams.get('payment');
    const amount = searchParams.get('amount');
    if (payment === 'success') toast.success(`IPTV Payment successful! Rs.${amount || ''} added`);
    else if (payment === 'failed') toast.error('IPTV Payment failed. Please try again.');
  }, [searchParams]);

  useEffect(() => {
    if (!token) { navigate(basePath); return; }
    init();
  }, []); // eslint-disable-line

  // Arriving from dashboard "Recharge Now" for IPTV: open the recharge dialog
  // for the matching subscriber once subscriptions are loaded.
  useEffect(() => {
    if (pendingRecharge && subscriptions.length > 0) {
      const m = (pendingRecharge.mobile || '').replace(/\D/g, '');
      const sub =
        subscriptions.find(s => s.subscription_id === pendingRecharge.subscriptionId) ||
        subscriptions.find(s => (s.mobile_no || '').replace(/\D/g, '') === m);
      setTab('dashboard');
      if (sub) {
        openRenewDialog(sub);
      } else {
        toast.error('Could not find this subscriber. Showing your subscriptions.');
      }
      setPendingRecharge(null);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [subscriptions, pendingRecharge]); // eslint-disable-line

  const init = async () => {
    setLoading(true);
    // Resolve role first so subsequent fetches pick the right endpoints
    let lcoFlag = false;
    let scopedCid = null;
    try {
      const res = await fetch(`${API}/api/portal/my-services`, { headers: authHeader });
      if (res.ok) {
        const data = await res.json();
        const customersArr = data.customers || [];
        setAllCustomers(customersArr);
        const storedActive = localStorage.getItem('portal_active_customer_id');
        const activeCust =
          (storedActive && customersArr.find(c => c.customer_id === storedActive)) ||
          customersArr[0];
        const partner = customersArr.find(c =>
          (c.customer_type === 'partner' || c.customer_type === 'bulk_bandwidth') && c.iptv_access_token
        );
        const distributor = customersArr.find(c => c.customer_type === 'iptv_distributor');
        const lco = customersArr.find(c => !!c.parent_partner_id && c.customer_type !== 'iptv_distributor');
        if (!partner && distributor) {
          // Distributor role — a dedicated Distributor Portal view is rendered
          setIsDistributor(true);
          scopedCid = distributor.customer_id;
        } else if (!partner && lco) {
          lcoFlag = true;
          scopedCid = activeCust?.parent_partner_id ? activeCust.customer_id : lco.customer_id;
        } else {
          scopedCid = activeCust?.customer_id || customersArr[0]?.customer_id || null;
        }
      }
    } catch (e) { /* fall through; partner-auth helpers will surface errors */ }
    setIsLco(lcoFlag);
    setLcoCustomerId(scopedCid);
    await Promise.all([
      fetchDashboard(),
      fetchPackages(),
      fetchStates(),
      lcoFlag ? Promise.resolve() : fetchLcos(),         // partner-only
      fetchSubscriptions(lcoFlag, scopedCid),
      fetchIptvWallet(scopedCid),
    ]);
    setLoading(false);
  };

  const fetchAll = async () => init();

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API}/api/portal/iptv/dashboard`, { headers: authHeader });
      if (res.status === 401) { navigate(basePath); return; }
      if (res.status === 403) { const d = await res.json(); toast.error(getError(d, 'Access denied')); navigate(`${basePath}/dashboard`); return; }
      if (res.ok) setDashboard(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchPackages = async () => {
    try {
      const res = await fetch(`${API}/api/portal/iptv/packages`, { headers: authHeader });
      if (res.ok) { const d = await res.json(); setPackages(d.packages || []); }
    } catch (e) { console.error(e); }
  };

  const fetchStates = async () => {
    try {
      const res = await fetch(`${API}/api/portal/iptv/state-city`, { headers: authHeader });
      if (res.ok) { const data = await res.json(); setStates(Array.isArray(data) ? data : []); }
    } catch (e) { console.error(e); }
  };

  const fetchLcos = async () => {
    try {
      const res = await fetch(`${API}/api/portal/iptv/lco/list`, { headers: authHeader });
      if (res.ok) { const d = await res.json(); setLcos(d.lcos || []); }
    } catch (e) { console.error(e); }
  };

  const fetchSubscriptions = async (lcoFlagArg, scopedCidArg) => {
    const useLco = lcoFlagArg ?? isLco;
    const cid = scopedCidArg ?? lcoCustomerId;
    try {
      // LCO: always query-param scoped. Partner: query-param scoped by current customer_id
      // (not partner-auth) so that mixed-linked portal users never see another customer's list.
      const url = cid
        ? `${API}/api/iptv/portal/subscriptions?customer_id=${cid}`
        : `${API}/api/portal/iptv/subscriptions`;
      const res = await fetch(url, cid ? {} : { headers: authHeader });
      if (res.ok) { const d = await res.json(); setSubscriptions(d.subscriptions || []); }
    } catch (e) { console.error(e); }
  };

  const fetchIptvWallet = async (scopedCidArg) => {
    // Always scope reads to a specific customer_id to prevent cross-account leakage
    // when a portal_user is linked to multiple customers.
    const cid = scopedCidArg ?? lcoCustomerId;
    if (!cid) return;
    try {
      const [balRes, txnRes] = await Promise.all([
        fetch(`${API}/api/iptv/portal/balance?customer_id=${cid}`),
        fetch(`${API}/api/iptv/portal/transactions?customer_id=${cid}&limit=30`)
      ]);
      if (balRes.ok) { const d = await balRes.json(); setIptvBalance(d.balance || 0); }
      if (txnRes.ok) { const d = await txnRes.json(); setIptvTransactions(d.transactions || []); }
    } catch (e) { console.error(e); }
  };

  const selectedCities = states.find(s => s.state_code === subForm.state)?.cities || [];

  // Subscribe
  const handleSubscribe = async () => {
    if (!subForm.mobile_no || !subForm.subscriber_name || !subForm.state || !subForm.city || !showSubscribe) {
      toast.error('Please fill mobile, name, state and city'); return;
    }
    // Policy: Partners must assign an LCO/Distributor before activating.
    if (isPartner && !(subForm.lco_customer_id && subForm.lco_customer_id.trim())) {
      setShowSubscribe(null);
      setShowAssignLcoDialog({
        title: 'Assign an LCO/Distributor first',
        message: (
          "Direct customer activation isn't allowed. Please create (or pick) "
          + "an LCO or Distributor and subscribe on their behalf."
        ),
      });
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...subForm, package_id: showSubscribe.package_id };
      if (!payload.lco_customer_id || !payload.lco_customer_id.trim()) delete payload.lco_customer_id;
      const res = await fetch(`${API}/api/portal/iptv/subscribe`, {
        method: 'POST', headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message);
        if (!isLco && !isDistributor && data.commission_earned > 0) toast.success(`Commission: Rs.${data.commission_earned.toFixed(2)}`);
        setShowSubscribe(null);
        setSubForm({ mobile_no: '', subscriber_name: '', state: '', city: '', address: '', pincode: '', lco_customer_id: '' });
        fetchDashboard(); fetchSubscriptions(); fetchIptvWallet();
      } else if (res.status === 403) {
        setShowSubscribe(null);
        setShowAssignLcoDialog({
          title: 'Assign an LCO/Distributor first',
          message: getError(data, 'Direct activation is not allowed.'),
        });
      } else { toast.error(getError(data, 'Subscription failed')); }
    } catch { toast.error('Connection error. Please try again.'); }
    finally { setSubmitting(false); }
  };

  // Renew
  const handleRenew = async (sub) => {
    // Policy (Sep 2026): only IPTV Distributors must route through an LCO.
    // Regular Partners can renew directly.
    if (isDistributor && (!sub.lco_customer_id || sub.lco_customer_id === sub.customer_id || sub.lco_customer_id === sub.partner_id)) {
      setShowRenewDialog(null);
      setShowAssignLcoDialog({
        title: 'Assign this subscription to one of your LCOs',
        message: (
          "As an IPTV Distributor you can't renew a direct subscription. "
          + "Pick or create an LCO and re-subscribe this customer under them."
        ),
        subscription: sub,
      });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/portal/iptv/renew`, {
        method: 'POST', headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscription_id: sub.subscription_id })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message || 'Renewed successfully');
        setShowRenewDialog(null); fetchDashboard(); fetchSubscriptions(); fetchIptvWallet();
      } else if (res.status === 403) {
        setShowRenewDialog(null);
        setShowAssignLcoDialog({
          title: 'This subscription needs an LCO/Distributor',
          message: getError(data, 'Renewal blocked — please assign an LCO/Distributor.'),
          subscription: sub,
        });
      } else { toast.error(getError(data, 'Renewal failed')); }
    } catch { toast.error('Connection error.'); }
    finally { setSubmitting(false); }
  };

  // Switch Package (cancel old + create new on a different package)
  const handleSwitchPackage = async (sub, newPackageId) => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/portal/iptv/switch-package`, {
        method: 'POST', headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscription_id: sub.subscription_id,
          new_package_id: newPackageId,
          lco_customer_id: sub.lco_customer_id || undefined,
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message || 'Package switched. Subscriber must re-login with OTP.');
        setShowRenewDialog(null); fetchDashboard(); fetchSubscriptions(); fetchIptvWallet();
      } else { toast.error(getError(data, 'Package switch failed')); }
    } catch { toast.error('Connection error.'); }
    finally { setSubmitting(false); }
  };

  // When opening the renew dialog, default the package selector to the current pack
  const openRenewDialog = async (sub) => {
    setSwitchPackageId(sub.package_id || '');
    setRenewStatus({ loading: true, data: null });
    setShowRenewDialog(sub);
    try {
      const res = await fetch(`${API}/api/portal/iptv/subscription-status/${sub.subscription_id}`, { headers: authHeader });
      const data = await res.json();
      setRenewStatus({ loading: false, data: res.ok ? data : null });
    } catch { setRenewStatus({ loading: false, data: null }); }
  };

  // Pre-Renew
  const handlePreRenew = async (sub) => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/portal/iptv/pre-renew`, {
        method: 'POST', headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscription_id: sub.subscription_id })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message || 'Pre-renewed successfully');
        setShowPreRenewDialog(null); fetchDashboard(); fetchSubscriptions(); fetchIptvWallet();
      } else { toast.error(getError(data, 'Pre-renewal failed')); }
    } catch { toast.error('Connection error.'); }
    finally { setSubmitting(false); }
  };

  // Lookup
  const handleLookup = async () => {
    if (!lookupPhone) { toast.error('Enter mobile number'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/portal/iptv/subscription/${lookupPhone}`, { headers: authHeader });
      const data = await res.json();
      if (res.ok) { setLookupResult(data.subscriptions || []); }
      else { toast.error(getError(data, 'Subscription not found')); setLookupResult(null); }
    } catch { toast.error('Connection error'); }
    finally { setSubmitting(false); }
  };

  // Check live provider status
  const handleCheckStatus = async (sub) => {
    setStatusDialog({ loading: true, data: null, sub });
    try {
      const res = await fetch(`${API}/api/portal/iptv/subscription-status/${sub.subscription_id}`, {
        headers: authHeader
      });
      const data = await res.json();
      if (res.ok) {
        setStatusDialog({ loading: false, data, sub });
      } else {
        toast.error(getError(data, 'Could not fetch status'));
        setStatusDialog(null);
      }
    } catch { toast.error('Connection error'); setStatusDialog(null); }
  };

  // Sync local record from provider (Check Status dialog)
  const handleSyncFromProvider = async (sub) => {
    setStatusDialog(prev => ({ ...prev, syncing: true }));
    try {
      const res = await fetch(`${API}/api/portal/iptv/subscription/${sub.subscription_id}/sync`, {
        method: 'POST', headers: authHeader
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message || 'Local record updated');
        setStatusDialog(null);
        setShowRenewDialog(null);
        fetchSubscriptions();
      } else {
        toast.error(getError(data, 'Sync failed'));
        setStatusDialog(prev => ({ ...prev, syncing: false }));
      }
    } catch { toast.error('Connection error'); setStatusDialog(prev => ({ ...prev, syncing: false })); }
  };

  // Delete
  const handleDelete = async () => {
    if (!deletePhone) { toast.error('Enter mobile number'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/portal/iptv/subscription`, {
        method: 'DELETE', headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile_no: deletePhone })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message); setShowDelete(false); setDeletePhone('');
        fetchDashboard(); fetchSubscriptions();
      } else { toast.error(getError(data, 'Deletion failed')); }
    } catch { toast.error('Connection error'); }
    finally { setSubmitting(false); }
  };

  // Set Pricing
  const handleSetPricing = async () => {
    const price = parseFloat(pricingForm.lco_price);
    if (!price || price <= 0 || !showSetPricing) { toast.error('Enter valid price'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/portal/iptv/pricing/${showSetPricing.package_id}`, {
        method: 'PUT', headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ package_id: showSetPricing.package_id, lco_price: price })
      });
      const data = await res.json();
      if (res.ok) { toast.success(data.message); setShowSetPricing(null); fetchPackages(); }
      else { toast.error(getError(data, 'Failed to set pricing')); }
    } catch { toast.error('Connection error'); }
    finally { setSubmitting(false); }
  };

  // IPTV Top-up
  const handleTopUp = async () => {
    const amt = parseFloat(topUpAmount);
    if (!amt || amt <= 0) { toast.error('Enter valid amount'); return; }
    setTopUpLoading(true);
    try {
      const res = await fetch(`${API}/api/portal/iptv/wallet/topup`, {
        method: 'POST', headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: amt })
      });
      const data = await res.json();
      if (res.ok && data.success && data.payment_url) {
        window.location.href = data.payment_url;
      } else { toast.error(getError(data, 'Top-up initiation failed')); }
    } catch { toast.error('Connection error'); }
    finally { setTopUpLoading(false); }
  };

  // Credit LCO IPTV Wallet
  const handleCreditLCO = async () => {
    const amt = parseFloat(creditAmount);
    if (!amt || amt <= 0 || !showCreditLCO) { toast.error('Enter valid amount'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/portal/iptv/wallet/credit-lco`, {
        method: 'POST', headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ lco_customer_id: showCreditLCO.customer_id, amount: amt })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message);
        setShowCreditLCO(null); setCreditAmount('');
        fetchLcos(); fetchIptvWallet();
      } else { toast.error(getError(data, 'Credit failed')); }
    } catch { toast.error('Connection error'); }
    finally { setSubmitting(false); }
  };

  const filteredPackages = packages.filter(p =>
    !searchTerm || p.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const _parseEndDate = (endDate) => {
    const parts = endDate.split('-');
    const end = parts.length === 3 && parts[2].length === 4
      ? new Date(parts[2], parts[1] - 1, parts[0]) : new Date(endDate);
    end.setHours(0, 0, 0, 0); // date-only: subscription is valid through the whole end day
    return end;
  };

  const isExpiringSoon = (endDate) => {
    if (!endDate) return false;
    try {
      const end = _parseEndDate(endDate);
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const diff = Math.round((end - today) / (1000 * 60 * 60 * 24));
      return diff >= 0 && diff <= 7;
    } catch { return false; }
  };

  const isExpired = (endDate) => {
    if (!endDate) return false;
    try {
      const end = _parseEndDate(endDate);
      const today = new Date(); today.setHours(0, 0, 0, 0);
      return end < today; // expired only the day AFTER the end date
    } catch { return false; }
  };

  const subIsExpired = (s) => isExpired(s.end_date) || s.status === 'expired';
  const filteredSubscriptions = subscriptions.filter(s => {
    const expired = subIsExpired(s);
    const expiring = !expired && isExpiringSoon(s.end_date);
    if (subFilter === 'active' && expired) return false;
    if (subFilter === 'expiring' && !expiring) return false;
    if (subFilter === 'expired' && !expired) return false;
    const q = subSearch.trim().toLowerCase();
    if (!q) return true;
    const digits = q.replace(/\D/g, '');
    return (s.subscriber_name || '').toLowerCase().includes(q) ||
      (digits && (s.mobile_no || '').replace(/\D/g, '').includes(digits));
  });
  const expiredCount = subscriptions.filter(subIsExpired).length;

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="partner-iptv">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(`${basePath}/dashboard`)} data-testid="back-btn">
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Monitor className="h-5 w-5 text-indigo-600" /> IPTV Management
              </h1>
              <p className="text-xs text-slate-500">ADIGITAL IPTV Subscriptions</p>
            </div>
          </div>
          <div className="flex gap-2">
            {allCustomers.length > 1 && (
              <CustomerSwitcher customers={allCustomers} activeId={activeId} onChange={setActiveId} />
            )}
            {(isLco || isDistributor) && (
              <Button variant="outline" size="sm"
                onClick={() => navigate('/portal/care-pricing')}
                data-testid="care-pricing-nav-btn">
                <IndianRupee className="h-4 w-4 mr-1" /> Customer Pricing
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => { setShowLookup(true); setLookupResult(null); setLookupPhone(''); }} data-testid="lookup-btn">
              <Search className="h-4 w-4 mr-1" /> Lookup
            </Button>
            <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => { setShowDelete(true); setDeletePhone(''); }} data-testid="delete-btn">
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4 space-y-4">
        {/* Summary Cards — LCOs get Active Subs, IPTV Wallet, Packages (3 cards).
            Partner + Distributor see IPTV Wallet, Commission (partner only), Packages
            since the SubscriptionBreakdown below already shows Active/Expired per tier. */}
        <div className={`grid grid-cols-2 gap-3 ${isLco ? 'md:grid-cols-3' : isDistributor ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
          {isLco && (
            <Card><CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-1"><Monitor className="h-4 w-4" /> Active Subs</div>
              <div className="text-2xl font-bold text-slate-800" data-testid="iptv-active-count">{dashboard.active_subscriptions || 0}</div>
            </CardContent></Card>
          )}
          <Card className="cursor-pointer hover:ring-2 ring-indigo-200" onClick={() => setTab('wallet')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-1"><Wallet className="h-4 w-4" /> IPTV Wallet</div>
              <div className="text-2xl font-bold text-green-600" data-testid="iptv-wallet-balance">Rs.{iptvBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
            </CardContent>
          </Card>
          {!isLco && !isDistributor && (
            <Card><CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-1"><TrendingUp className="h-4 w-4" /> Commission</div>
              <div className="text-2xl font-bold text-amber-600">Rs.{(dashboard.total_commission || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
            </CardContent></Card>
          )}
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1"><Package className="h-4 w-4" /> Packages</div>
            <div className="text-2xl font-bold text-indigo-600">{dashboard.packages_count || packages.length}</div>
          </CardContent></Card>
        </div>

        {/* Tier Breakdown — Partner (Self / Distributor / LCO), Distributor (Self / LCO) */}
        {dashboard.subscription_breakdown && (
          <SubscriptionBreakdown breakdown={dashboard.subscription_breakdown} viewerType={dashboard.viewer_type} />
        )}

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
              <ListFilter className="h-4 w-4 mr-1" /> Subscriptions
            </TabsTrigger>
            <TabsTrigger value="packages" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
              <Package className="h-4 w-4 mr-1" /> Packages
            </TabsTrigger>
            <TabsTrigger value="wallet" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
              <Wallet className="h-4 w-4 mr-1" /> IPTV Wallet
            </TabsTrigger>
            {!isLco && !isDistributor && (
              <TabsTrigger value="lcos" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
                <Users className="h-4 w-4 mr-1" /> LCO Management
              </TabsTrigger>
            )}
            {!isLco && !isDistributor && (
              <TabsTrigger value="pricing" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
                <Settings2 className="h-4 w-4 mr-1" /> LCO Pricing
              </TabsTrigger>
            )}
            {!isLco && !isDistributor && (
              <TabsTrigger value="distributors" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700" data-testid="distributors-tab">
                <Users className="h-4 w-4 mr-1" /> Distributors
              </TabsTrigger>
            )}
            {isDistributor && (
              <TabsTrigger value="my-distributor" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700" data-testid="my-distributor-tab">
                <Users className="h-4 w-4 mr-1" /> My LCOs & API Key
              </TabsTrigger>
            )}
            {!isLco && (
              <TabsTrigger value="audit" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700" data-testid="partner-audit-tab">
                <History className="h-4 w-4 mr-1" /> Audit Trail
              </TabsTrigger>
            )}
          </TabsList>

          {/* Subscriptions Tab */}
          <TabsContent value="dashboard">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="text-base">My Subscriptions ({filteredSubscriptions.length}{(subFilter !== 'all' || subSearch) ? ` of ${subscriptions.length}` : ''})</CardTitle>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="flex items-center gap-1 flex-wrap">
                      {[
                        { key: 'all', label: `All (${subscriptions.length})` },
                        { key: 'active', label: 'Active' },
                        { key: 'expiring', label: 'Expiring' },
                        { key: 'expired', label: `Expired (${expiredCount})` },
                      ].map(f => (
                        <button
                          key={f.key}
                          onClick={() => setSubFilter(f.key)}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                            subFilter === f.key
                              ? f.key === 'expired' ? 'bg-red-600 text-white border-red-600' : 'bg-indigo-600 text-white border-indigo-600'
                              : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                          }`}
                          data-testid={`sub-filter-${f.key}`}
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                    <div className="relative w-full sm:w-56">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input placeholder="Search name or mobile..." value={subSearch} onChange={e => setSubSearch(e.target.value)} className="pl-9 h-8 text-sm" data-testid="sub-search-input" />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredSubscriptions.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <Monitor className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">{subscriptions.length === 0 ? 'No subscriptions yet' : 'No subscribers match this filter'}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredSubscriptions.map((s) => {
                      const expired = subIsExpired(s);
                      const expiring = !expired && isExpiringSoon(s.end_date);
                      return (
                        <div key={s.subscription_id} className={`p-3 rounded-lg border ${expiring ? 'border-amber-300 bg-amber-50/50' : expired ? 'border-red-200 bg-red-50/30' : ''}`} data-testid={`sub-${s.subscription_id}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-sm">{s.subscriber_name}</div>
                              <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5">
                                <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{s.mobile_no}</span>
                                <span>{s.package_name}</span>
                                <span>{s.start_date} - {s.end_date}</span>
                              </div>
                              {s.lco_customer_id && s.lco_customer_id !== s.customer_id && <span className="text-[10px] text-slate-400 mt-0.5 block">LCO: {s.customer_name}</span>}
                            </div>
                            <div className="flex items-center gap-2">
                              {s.amount_charged > 0 && <span className="text-sm font-semibold">Rs.{s.amount_charged}</span>}
                              {expiring && <Badge className="bg-amber-100 text-amber-700"><Clock className="h-3 w-3 mr-1" />Expiring</Badge>}
                              {expired && <Badge className="bg-red-100 text-red-700">Expired</Badge>}
                              {s.status === 'active' && !expired && <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="h-3 w-3 mr-1" />Active</Badge>}
                              {needsLcoAssignment(s) && (
                                <Badge
                                  className="bg-amber-100 text-amber-800 border border-amber-300"
                                  title="Assign this subscriber under an LCO/Distributor before renewal — Partners can no longer renew directly."
                                  data-testid={`needs-lco-chip-${s.subscription_id}`}
                                >
                                  <AlertTriangle className="h-3 w-3 mr-1" /> Needs LCO
                                </Badge>
                              )}
                              {(s.status === 'active' || s.status === 'expired') && (
                                <div className="flex gap-1">
                                  {!expired && (
                                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setShowPreRenewDialog(s)} data-testid={`prerenew-${s.subscription_id}`}>
                                      <CalendarClock className="h-3 w-3 mr-1" /> Pre-Renew
                                    </Button>
                                  )}
                                  <Button variant={expired ? 'default' : 'outline'} size="sm" className={`h-7 text-xs ${expired ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`} onClick={() => openRenewDialog(s)} data-testid={`renew-${s.subscription_id}`}>
                                    <RotateCcw className="h-3 w-3 mr-1" /> {expired ? 'Recharge' : 'Renew'}
                                  </Button>
                                </div>
                              )}
                              <Button
                                variant="outline" size="sm"
                                className="h-7 text-xs"
                                title="Check live provider status"
                                onClick={() => handleCheckStatus(s)}
                                data-testid={`check-status-${s.subscription_id}`}
                              >
                                <Info className="h-3 w-3 mr-1" /> Check Status
                              </Button>
                              <Button
                                variant="ghost" size="sm"
                                className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Delete this subscription"
                                onClick={() => { setDeletePhone(s.mobile_no); setShowDelete(true); }}
                                data-testid={`delete-sub-${s.subscription_id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Packages */}
          <TabsContent value="packages">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">IPTV Packages ({packages.length})</CardTitle>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input placeholder="Search packages..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 h-9" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-2">
                    {filteredPackages.length === 0 ? (
                      <div className="text-center py-12 text-slate-400">
                        <Package className="h-12 w-12 mx-auto mb-3 opacity-50" /><p className="text-sm">No packages available</p>
                      </div>
                    ) : filteredPackages.map(p => {
                      const shownPrice = isLco
                        ? (p.effective_price ?? p.lco_price ?? p.partner_price ?? p.base_price)
                        : isDistributor
                          ? (p.distributor_price ?? p.partner_price ?? p.base_price)
                          : (p.partner_price ?? p.base_price);
                      return (
                      <div key={p.package_id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50" data-testid={`pkg-${p.package_id}`}>
                        <div className="flex-1">
                          <div className="font-medium text-sm flex items-center gap-2">
                            <Monitor className="h-4 w-4 text-indigo-600" /> {p.name}
                          </div>
                          <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
                            <span>{p.channels?.length || 0} channels</span>
                            <span className="font-semibold text-indigo-700">Rs.{shownPrice}</span>
                            <span>{p.validity_days} days</span>
                            {isLco && p.price_source === 'parent_partner_price_fallback' && (
                              <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50 text-[10px]">LCO price not set — awaiting partner</Badge>
                            )}
                          </div>
                        </div>
                        <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700"
                          onClick={() => { setShowSubscribe(p); setSubForm({ mobile_no: '', subscriber_name: '', state: '', city: '', address: '', pincode: '', lco_customer_id: '' }); }}
                          data-testid={`subscribe-${p.package_id}`}>
                          <Play className="h-3.5 w-3.5 mr-1" /> Subscribe
                        </Button>
                      </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* IPTV Wallet Tab */}
          <TabsContent value="wallet">
            <div className="space-y-4">
              {/* Wallet Balance Card */}
              <Card className="bg-gradient-to-br from-indigo-900 to-indigo-800 text-white">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-indigo-300 text-sm">IPTV Wallet Balance</p>
                      <p className="text-3xl font-bold mt-1" data-testid="iptv-wallet-main-balance">
                        Rs.{iptvBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </p>
                      <p className="text-indigo-400 text-xs mt-1">Separate from OTT Wallet</p>
                    </div>
                    <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={() => { setShowTopUp(true); setTopUpAmount(''); }} data-testid="iptv-topup-btn">
                      <Plus className="h-3 w-3 mr-1" /> Top Up
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Transactions */}
              <Card>
                <CardHeader className="pb-3"><CardTitle className="text-base">IPTV Wallet Transactions</CardTitle></CardHeader>
                <CardContent>
                  {iptvTransactions.length === 0 ? (
                    <div className="text-center py-8 text-slate-400"><p className="text-sm">No transactions yet</p></div>
                  ) : (
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-2">
                        {iptvTransactions.map(t => (
                          <div key={t.transaction_id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`iptv-txn-${t.transaction_id}`}>
                            <div className="flex items-center gap-3">
                              <div className={`p-1.5 rounded-full ${t.type === 'credit' ? 'bg-green-100' : 'bg-red-100'}`}>
                                {t.type === 'credit' ? <ArrowDownRight className="h-4 w-4 text-green-600" /> : <ArrowUpRight className="h-4 w-4 text-red-600" />}
                              </div>
                              <div>
                                <p className="text-sm font-medium">{t.description}</p>
                                <p className="text-xs text-slate-400">{new Date(t.created_at).toLocaleString()}</p>
                              </div>
                            </div>
                            <span className={`font-semibold text-sm ${t.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                              {t.type === 'credit' ? '+' : '-'}Rs.{t.amount?.toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* LCO Management Tab */}
          <TabsContent value="lcos">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <CardTitle className="text-base">LCO Management ({lcos.length})</CardTitle>
                    <p className="text-xs text-slate-500">Manage LCO IPTV wallets and credits</p>
                  </div>
                  <div className="relative w-full sm:w-56">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input placeholder="Search LCO name or phone..." value={lcoSearch} onChange={e => setLcoSearch(e.target.value)} className="pl-9 h-8 text-sm" data-testid="iptv-lco-search-input" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {lcos.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No LCOs found. Add LCOs from the OTT Manage LCOs page.</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate(`${basePath}/lcos`)}>
                      Go to Manage LCOs
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {lcos.filter(l => {
                      const q = lcoSearch.trim().toLowerCase();
                      if (!q) return true;
                      return (l.company_name || '').toLowerCase().includes(q) ||
                        (l.contact_name || '').toLowerCase().includes(q) ||
                        (l.contact_phone || '').includes(q.replace(/\D/g, '') || q);
                    }).map(lco => (
                      <div key={lco.customer_id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50" data-testid={`iptv-lco-${lco.customer_id}`}>
                        <div>
                          <div className="font-medium text-sm">{lco.company_name || lco.contact_name}</div>
                          <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5">
                            <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{lco.contact_phone}</span>
                            <span>OTT: Rs.{(lco.ott_wallet_balance || 0).toFixed(2)}</span>
                            <span className="font-semibold text-indigo-600">IPTV: Rs.{(lco.iptv_wallet_balance || 0).toFixed(2)}</span>
                          </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => { setShowCreditLCO(lco); setCreditAmount(''); }} data-testid={`credit-iptv-lco-${lco.customer_id}`}>
                          <IndianRupee className="h-3.5 w-3.5 mr-1" /> Credit IPTV
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* LCO Pricing */}
          <TabsContent value="pricing">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">LCO Pricing</CardTitle>
                <p className="text-xs text-slate-500">Set the price you charge LCOs for each IPTV package</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {packages.map(p => (
                    <div key={p.package_id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50">
                      <div>
                        <div className="font-medium text-sm">{p.name}</div>
                        <div className="text-xs text-slate-500 flex items-center gap-3">
                          <span>{p.channels?.length || 0} channels</span>
                          <span>Your price: Rs.{isDistributor ? (p.distributor_price ?? p.partner_price ?? p.base_price) : (p.partner_price || p.base_price)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {p.lco_price > 0 ? (
                          <Badge variant="outline" className="text-green-700 border-green-200 bg-green-50">LCO: Rs.{p.lco_price}</Badge>
                        ) : (
                          <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">Not Set</Badge>
                        )}
                        <Button variant="outline" size="sm"
                          onClick={() => { setShowSetPricing(p); setPricingForm({ lco_price: p.lco_price > 0 ? String(p.lco_price) : '' }); }}>
                          <IndianRupee className="h-3.5 w-3.5 mr-1" /> Set Price
                        </Button>
                      </div>
                    </div>
                  ))}
                  {packages.length === 0 && (
                    <div className="text-center py-8 text-slate-400"><p className="text-sm">No packages available.</p></div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Distributors Tab — Partner view (spec 1c self-serve + spec 2a pricing) */}
          {!isLco && !isDistributor && (
            <TabsContent value="distributors">
              <IPTVDistributorsPanel role="partner" packages={packages} />
            </TabsContent>
          )}

          {/* My LCOs Tab — Distributor view (spec 3b own API key + LCO management) */}
          {isDistributor && (
            <TabsContent value="my-distributor">
              <IPTVDistributorsPanel role="distributor" packages={packages} />
            </TabsContent>
          )}
          {!isLco && (
            <TabsContent value="audit">
              <PartnerAuditTrail />
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Subscribe Dialog */}
      <Dialog open={!!showSubscribe} onOpenChange={() => setShowSubscribe(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New IPTV Subscription</DialogTitle>
            <DialogDescription>{showSubscribe?.name} ({showSubscribe?.channels?.length || 0} channels) - Rs.{isLco ? (showSubscribe?.effective_price ?? showSubscribe?.lco_price ?? showSubscribe?.partner_price ?? showSubscribe?.base_price) : isDistributor ? (showSubscribe?.distributor_price ?? showSubscribe?.partner_price ?? showSubscribe?.base_price) : (showSubscribe?.partner_price ?? showSubscribe?.base_price)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {lcos.length > 0 && (
              <div><Label>For LCO (optional)</Label>
                <Select value={subForm.lco_customer_id} onValueChange={v => setSubForm(p => ({ ...p, lco_customer_id: v === '_self' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Self (Partner)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_self">Self (Partner)</SelectItem>
                    {lcos.map(l => <SelectItem key={l.customer_id} value={l.customer_id}>{l.company_name} (IPTV: Rs.{(l.iptv_wallet_balance || 0).toFixed(2)})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Mobile No *</Label><Input value={subForm.mobile_no} onChange={e => setSubForm(p => ({ ...p, mobile_no: e.target.value }))} placeholder="10-digit mobile" maxLength={10} data-testid="iptv-mobile" /></div>
            <div><Label>Subscriber Name *</Label><Input value={subForm.subscriber_name} onChange={e => setSubForm(p => ({ ...p, subscriber_name: e.target.value }))} placeholder="Name" data-testid="iptv-name" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>State *</Label>
                <Select value={subForm.state} onValueChange={v => setSubForm(p => ({ ...p, state: v, city: '' }))}>
                  <SelectTrigger data-testid="iptv-state"><SelectValue placeholder="Select State" /></SelectTrigger>
                  <SelectContent>{states.map(s => <SelectItem key={s.state_code} value={s.state_code}>{s.state_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>City *</Label>
                <Select value={subForm.city} onValueChange={v => setSubForm(p => ({ ...p, city: v }))} disabled={!subForm.state}>
                  <SelectTrigger data-testid="iptv-city"><SelectValue placeholder="Select City" /></SelectTrigger>
                  <SelectContent>{selectedCities.map(c => <SelectItem key={c.city_code} value={c.city_code}>{c.city_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Address</Label><Input value={subForm.address} onChange={e => setSubForm(p => ({ ...p, address: e.target.value }))} placeholder="Address" /></div>
            <div><Label>Pincode</Label><Input value={subForm.pincode} onChange={e => setSubForm(p => ({ ...p, pincode: e.target.value }))} placeholder="Pincode" maxLength={6} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubscribe(null)}>Cancel</Button>
            <Button onClick={handleSubscribe} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-subscribe">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Play className="h-4 w-4 mr-1" />} Subscribe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign LCO/Distributor dialog — surfaces the new Partner-can't-
          activate-directly policy in a helpful way. */}
      <Dialog open={!!showAssignLcoDialog} onOpenChange={() => setShowAssignLcoDialog(null)}>
        <DialogContent className="max-w-md" data-testid="assign-lco-dialog">
          <DialogHeader>
            <DialogTitle>{showAssignLcoDialog?.title || 'Assign an LCO/Distributor'}</DialogTitle>
            <DialogDescription>
              {showAssignLcoDialog?.message}
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-slate-600 bg-slate-50 border rounded-md p-3">
            <p className="font-medium mb-1">What to do next</p>
            <ol className="list-decimal pl-4 space-y-0.5 text-xs text-slate-500">
              <li>Open <span className="font-medium">LCO / Distributor Management</span></li>
              <li>Create the LCO (or promote an existing customer to LCO)</li>
              <li>Come back here and subscribe the customer under that LCO</li>
            </ol>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignLcoDialog(null)} data-testid="assign-lco-cancel">
              Not now
            </Button>
            <Button onClick={() => { setShowAssignLcoDialog(null); navigate('/portal/lcos'); }}
              className="bg-indigo-600 hover:bg-indigo-700" data-testid="assign-lco-goto-btn">
              Go to LCO Management
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Renew Dialog */}
      <Dialog open={!!showRenewDialog} onOpenChange={() => setShowRenewDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Recharge Subscription</DialogTitle>
            <DialogDescription>{showRenewDialog?.subscriber_name} ({showRenewDialog?.mobile_no})</DialogDescription>
          </DialogHeader>
          {(() => {
            const isSwitch = switchPackageId && switchPackageId !== showRenewDialog?.package_id;
            const selectedPkg = packages.find(p => p.package_id === switchPackageId);
            return (
              <div className="space-y-3 py-2 text-sm">
                {needsLcoAssignment(showRenewDialog) && (
                  <div
                    className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-800"
                    data-testid="renew-needs-lco-banner"
                  >
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <div className="text-xs">
                      <p className="font-semibold">Assign this subscription to an LCO</p>
                      <p>
                        As an IPTV Distributor you can't renew a direct
                        subscription. Assign it under one of your LCOs and
                        re-subscribe the customer there. Clicking Renew below
                        will be blocked.
                      </p>
                    </div>
                  </div>
                )}
                <div>
                  <Label className="text-xs">Package</Label>
                  <Select value={switchPackageId} onValueChange={setSwitchPackageId}>
                    <SelectTrigger data-testid="renew-pkg-select"><SelectValue placeholder="Select package" /></SelectTrigger>
                    <SelectContent>
                      {packages.map(p => (
                        <SelectItem key={p.package_id} value={p.package_id} data-testid={`renew-pkg-option-${p.package_id}`}>
                          {p.name} — Rs.{p.effective_price ?? p.partner_price ?? p.base_price} / {p.validity_days}d
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400 mt-1">Current: {showRenewDialog?.package_name}</p>
                </div>
                <div className="text-slate-600">
                  <p>Current end date: <strong>{showRenewDialog?.end_date}</strong></p>
                  {!isSwitch && <p className="mt-1">Renew extends by {showRenewDialog?.validity_days || 30} days and deducts from your IPTV wallet.</p>}
                </div>
                {/* Live provider status verification */}
                <div className="rounded-md border bg-slate-50 p-3 text-xs" data-testid="renew-live-status">
                  {renewStatus?.loading ? (
                    <span className="flex items-center gap-2 text-slate-500"><Loader2 className="h-3 w-3 animate-spin" /> Verifying live provider status…</span>
                  ) : renewStatus?.data ? (
                    <div className="space-y-1">
                      <p className="flex items-center gap-1.5 font-medium">
                        {renewStatus.data.provider_active
                          ? <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Active on provider</>
                          : <><XCircle className="h-3.5 w-3.5 text-red-500" /> Not active on provider</>}
                      </p>
                      <p className="text-slate-500">{renewStatus.data.provider_message}</p>
                      {renewStatus.data.provider_active && renewStatus.data.provider_end_date && (
                        <div className="pt-1">
                          <p className="text-slate-500">Provider end date: <strong>{renewStatus.data.provider_end_date}</strong></p>
                          <Button
                            size="sm" variant="secondary" className="mt-1 h-7 text-xs"
                            onClick={() => handleSyncFromProvider(showRenewDialog)}
                            data-testid="renew-sync-btn"
                          >
                            <RotateCcw className="h-3 w-3 mr-1" /> Update local record from provider (no charge)
                          </Button>
                        </div>
                      )}
                      {!renewStatus.data.provider_active && (
                        <p className="text-slate-500">Clicking Renew will automatically re-activate this subscription with the provider.</p>
                      )}
                    </div>
                  ) : (
                    <span className="text-slate-400">Live status unavailable — Renew will still process.</span>
                  )}
                </div>
                {isSwitch && (
                  <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-800" data-testid="switch-warning">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <div className="text-xs">
                      <p className="font-semibold">Switching package</p>
                      <p>The current subscription will be cancelled and a new one created on <strong>{selectedPkg?.name}</strong>. The customer needs to re-login with OTP if you change the subscription.</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRenewDialog(null)}>Cancel</Button>
            {switchPackageId && switchPackageId !== showRenewDialog?.package_id ? (
              <Button onClick={() => handleSwitchPackage(showRenewDialog, switchPackageId)} disabled={submitting} className="bg-amber-600 hover:bg-amber-700" data-testid="submit-switch">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RotateCcw className="h-4 w-4 mr-1" />} Switch Package
              </Button>
            ) : (
              <Button onClick={() => handleRenew(showRenewDialog)} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-renew">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RotateCcw className="h-4 w-4 mr-1" />} Renew
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Check Status Dialog */}
      <Dialog open={!!statusDialog} onOpenChange={() => setStatusDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="status-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Info className="h-5 w-5" /> Subscription Status</DialogTitle>
            <DialogDescription>{statusDialog?.sub?.subscriber_name} ({statusDialog?.sub?.mobile_no})</DialogDescription>
          </DialogHeader>
          {statusDialog?.loading ? (
            <div className="py-6 flex items-center justify-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Checking live status…</div>
          ) : statusDialog?.data ? (
            <div className="py-2 space-y-3 text-sm">
              <div className={`rounded-md p-3 flex items-center gap-2 font-medium ${statusDialog.data.provider_active ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                {statusDialog.data.provider_active ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                {statusDialog.data.provider_active ? 'Active on provider' : 'Not active on provider'}
              </div>
              <p className="text-slate-500 text-xs">{statusDialog.data.provider_message}</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="rounded border p-2"><p className="text-slate-400">Package</p><p className="font-medium">{statusDialog.data.package_name || '—'}</p></div>
                <div className="rounded border p-2"><p className="text-slate-400">Local status</p><p className="font-medium capitalize">{statusDialog.data.local_status || '—'}</p></div>
                <div className="rounded border p-2"><p className="text-slate-400">Local end date</p><p className="font-medium">{statusDialog.data.local_end_date || '—'}</p></div>
                <div className="rounded border p-2"><p className="text-slate-400">Provider end date</p><p className="font-medium">{statusDialog.data.provider_end_date || '—'}</p></div>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog(null)} data-testid="status-close">Close</Button>
            {statusDialog?.data?.provider_active && (
              <Button
                variant="secondary"
                onClick={() => handleSyncFromProvider(statusDialog.sub)}
                disabled={statusDialog?.syncing}
                data-testid="status-sync"
              >
                {statusDialog?.syncing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RotateCcw className="h-4 w-4 mr-1" />} Update Local Record
              </Button>
            )}
            {statusDialog?.data && (
              <Button
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={() => { const s = statusDialog.sub; setStatusDialog(null); openRenewDialog(s); }}
                data-testid="status-renew"
              >
                <RotateCcw className="h-4 w-4 mr-1" /> Renew
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pre-Renew Dialog */}
      <Dialog open={!!showPreRenewDialog} onOpenChange={() => setShowPreRenewDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Pre-Renew Subscription</DialogTitle>
            <DialogDescription>Pre-renew {showPreRenewDialog?.subscriber_name} ({showPreRenewDialog?.mobile_no}) before expiry</DialogDescription>
          </DialogHeader>
          <div className="py-3 text-sm text-slate-600">
            <p>Current end date: <strong>{showPreRenewDialog?.end_date}</strong></p>
            <p className="mt-1">Extended from current end date by {showPreRenewDialog?.validity_days || 30} days.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreRenewDialog(null)}>Cancel</Button>
            <Button onClick={() => handlePreRenew(showPreRenewDialog)} disabled={submitting} className="bg-amber-600 hover:bg-amber-700" data-testid="submit-prerenew">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CalendarClock className="h-4 w-4 mr-1" />} Pre-Renew
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* IPTV Top-Up Dialog */}
      <Dialog open={showTopUp} onOpenChange={setShowTopUp}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Top Up IPTV Wallet</DialogTitle>
            <DialogDescription>Add funds to your IPTV wallet via payment gateway</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Amount (Rs.) *</Label>
              <Input type="number" value={topUpAmount} onChange={e => setTopUpAmount(e.target.value)} placeholder="Enter amount" min="1" data-testid="iptv-topup-amount" />
            </div>
            <div className="flex gap-2 flex-wrap">
              {[500, 1000, 2000, 5000, 10000].map(amt => (
                <Button key={amt} variant="outline" size="sm" onClick={() => setTopUpAmount(String(amt))} className="text-xs">Rs.{amt}</Button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTopUp(false)}>Cancel</Button>
            <Button onClick={handleTopUp} disabled={topUpLoading} className="bg-green-600 hover:bg-green-700" data-testid="submit-iptv-topup">
              {topUpLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CreditCard className="h-4 w-4 mr-1" />} Pay
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credit LCO IPTV Wallet Dialog */}
      <Dialog open={!!showCreditLCO} onOpenChange={() => setShowCreditLCO(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Credit LCO IPTV Wallet</DialogTitle>
            <DialogDescription>Transfer from your IPTV wallet to {showCreditLCO?.company_name}'s IPTV wallet</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-sm text-slate-600">
              <p>Your IPTV Balance: <strong className="text-green-600">Rs.{iptvBalance.toFixed(2)}</strong></p>
              <p>LCO IPTV Balance: <strong>Rs.{(showCreditLCO?.iptv_wallet_balance || 0).toFixed(2)}</strong></p>
            </div>
            <div>
              <Label>Amount (Rs.) *</Label>
              <Input type="number" value={creditAmount} onChange={e => setCreditAmount(e.target.value)} placeholder="Enter amount" min="1" data-testid="credit-lco-amount" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreditLCO(null)}>Cancel</Button>
            <Button onClick={handleCreditLCO} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-credit-lco">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <IndianRupee className="h-4 w-4 mr-1" />} Transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lookup Dialog */}
      <Dialog open={showLookup} onOpenChange={setShowLookup}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Subscription Lookup</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex gap-2">
              <Input value={lookupPhone} onChange={e => setLookupPhone(e.target.value)} placeholder="Enter mobile number" maxLength={10} data-testid="lookup-mobile" className="flex-1" />
              <Button onClick={handleLookup} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
            {lookupResult && (
              lookupResult.length === 0 ? (
                <div className="text-center py-6 text-slate-400"><AlertTriangle className="h-8 w-8 mx-auto mb-2" /><p className="text-sm">No subscription found</p></div>
              ) : (
                <ScrollArea className="h-60">
                  {lookupResult.map((s, i) => (
                    <div key={i} className="p-3 border rounded-lg mb-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{s.name || s.subscriber_name}</span>
                        <Badge className={s.subscription_status === 'Active' || s.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>{s.subscription_status || s.status}</Badge>
                      </div>
                      <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                        <div><Phone className="h-3 w-3 inline mr-1" />{s.mobile || s.mobile_no}</div>
                        <div>Activated: {s.activation_date || s.start_date} | Expires: {s.expiry_date || s.end_date}</div>
                      </div>
                    </div>
                  ))}
                </ScrollArea>
              )
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-red-600">Delete Subscription</DialogTitle><DialogDescription>This will permanently remove the active subscription.</DialogDescription></DialogHeader>
          <div className="py-2"><Label>Mobile No *</Label><Input value={deletePhone} onChange={e => setDeletePhone(e.target.value)} placeholder="10-digit mobile" maxLength={10} data-testid="delete-mobile" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDelete(false)}>Cancel</Button>
            <Button onClick={handleDelete} disabled={submitting} variant="destructive" data-testid="submit-delete">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />} Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Pricing Dialog */}
      <Dialog open={!!showSetPricing} onOpenChange={() => setShowSetPricing(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Set LCO Price</DialogTitle>
            <DialogDescription>{showSetPricing?.name} (Your cost: Rs.{showSetPricing?.partner_price || showSetPricing?.base_price})</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>LCO Price (Rs.) *</Label>
              <Input type="number" value={pricingForm.lco_price} onChange={e => setPricingForm({ lco_price: e.target.value })} placeholder="Enter price" min="0" data-testid="iptv-lco-price" />
              <p className="text-xs text-slate-500 mt-1">Must be greater than your cost price</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetPricing(null)}>Cancel</Button>
            <Button onClick={handleSetPricing} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-iptv-pricing">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Price'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function SubscriptionBreakdown({ breakdown, viewerType }) {
  const TIER_META = {
    self: {
      label: viewerType === 'distributor' ? 'Distributor Self' : 'Partner Self',
      color: 'from-indigo-500 to-indigo-600',
      badge: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      icon: '👤',
    },
    distributor: {
      label: 'Via Distributors',
      color: 'from-amber-500 to-amber-600',
      badge: 'bg-amber-50 text-amber-700 border-amber-200',
      icon: '🏢',
    },
    lco: {
      label: 'Via LCOs',
      color: 'from-teal-500 to-teal-600',
      badge: 'bg-teal-50 text-teal-700 border-teal-200',
      icon: '🏪',
    },
  };
  const tiers = viewerType === 'distributor' ? ['self', 'lco'] : ['self', 'distributor', 'lco'];
  const totalActive = tiers.reduce((n, t) => n + (breakdown[t]?.active || 0), 0);
  const totalExpired = tiers.reduce((n, t) => n + (breakdown[t]?.expired || 0), 0);

  return (
    <Card data-testid="subscription-breakdown">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700">Subscription Breakdown</h3>
          <div className="text-xs text-slate-500">
            Active {totalActive} · Expired {totalExpired}
          </div>
        </div>
        <div className={`grid grid-cols-1 gap-3 ${tiers.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
          {tiers.map((t) => {
            const meta = TIER_META[t];
            const b = breakdown[t] || { active: 0, expired: 0 };
            return (
              <div
                key={t}
                className="border rounded-lg p-3 hover:shadow-sm transition"
                data-testid={`breakdown-tier-${t}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">{meta.icon}</span>
                  <span className={`text-xs px-2 py-0.5 rounded border ${meta.badge}`}>{meta.label}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wide">Active</div>
                    <div className="text-2xl font-bold text-emerald-700" data-testid={`breakdown-${t}-active`}>{b.active}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wide">Expired</div>
                    <div className="text-2xl font-bold text-red-600" data-testid={`breakdown-${t}-expired`}>{b.expired}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}



function PartnerAuditTrail() {
  const [state, setState] = React.useState({ rows: [], total: 0, loading: true, error: null, scope: 0 });
  const [filters, setFilters] = React.useState({
    q: '', event_type: '', wallet_type: '', source: '', from_date: '', to_date: '',
  });

  const load = React.useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const token = localStorage.getItem('portal_token') || localStorage.getItem('token');
      const p = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
      p.append('limit', 200);
      const res = await fetch(`${API}/api/portal/audit-log/tree?${p}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 403) {
        const err = await res.json();
        setState({ rows: [], total: 0, loading: false, error: err.detail, scope: 0 });
        return;
      }
      const data = await res.json();
      setState({ rows: data.rows || [], total: data.total || 0, loading: false, error: null, scope: data.scope_customer_count || 0 });
    } catch (e) {
      setState({ rows: [], total: 0, loading: false, error: e.message, scope: 0 });
    }
  }, [filters]);

  React.useEffect(() => { load(); }, [load]);

  const fmtAmount = (n) => n == null ? '—' : `Rs.${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <History className="h-4 w-4" /> Audit Trail
        </CardTitle>
        <CardDescription>
          Every debit, credit, login, subscription and permission change across you and your downstream (distributors, LCOs). 12-month retention.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {state.error ? (
          <div className="p-6 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm" data-testid="audit-disabled-message">
            <AlertTriangle className="inline h-4 w-4 mr-2" />
            {state.error}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
              <Input placeholder="Search…" value={filters.q}
                     onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} data-testid="audit-search" />
              <Select value={filters.event_type || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, event_type: v === 'all' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Event" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All events</SelectItem>
                  <SelectItem value="wallet_topup">Wallet Top-up</SelectItem>
                  <SelectItem value="wallet_manual_credit">Manual Credit</SelectItem>
                  <SelectItem value="wallet_manual_debit">Manual Debit</SelectItem>
                  <SelectItem value="subscription_purchase">Subscription Purchase</SelectItem>
                  <SelectItem value="login">Login</SelectItem>
                  <SelectItem value="login_failed">Failed Login</SelectItem>
                  <SelectItem value="lco_create">LCO Created</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.wallet_type || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, wallet_type: v === 'all' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Wallet" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All wallets</SelectItem>
                  <SelectItem value="ott">OTT</SelectItem>
                  <SelectItem value="iptv">IPTV</SelectItem>
                  <SelectItem value="railtel">Railtel</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex gap-1">
                <Input type="date" value={filters.from_date} onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value }))} />
                <Input type="date" value={filters.to_date} onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value }))} />
              </div>
            </div>
            <div className="text-xs text-slate-500 mb-2">
              Showing {state.rows.length} of {state.total.toLocaleString()} rows across {state.scope} accounts
            </div>
            {state.loading ? (
              <div className="p-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></div>
            ) : (
              <div className="border rounded-md max-h-[600px] overflow-y-auto" data-testid="audit-tree-list">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2">When</th>
                      <th className="text-left p-2">Event</th>
                      <th className="text-left p-2">Actor</th>
                      <th className="text-left p-2">Target</th>
                      <th className="text-right p-2">Amount</th>
                      <th className="text-left p-2">IP / Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.rows.length === 0 && (
                      <tr><td colSpan="6" className="p-6 text-center text-slate-400">No activity yet.</td></tr>
                    )}
                    {state.rows.map((r) => (
                      <tr key={r.audit_id} className="border-t hover:bg-slate-50">
                        <td className="p-2 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                        <td className="p-2">
                          <Badge variant="outline" className={
                            r.category === 'financial' ? 'text-emerald-700 border-emerald-200 bg-emerald-50' :
                            r.category === 'security' ? 'text-rose-700 border-rose-200 bg-rose-50' :
                            'text-blue-700 border-blue-200 bg-blue-50'
                          }>{r.event_type}</Badge>
                        </td>
                        <td className="p-2">
                          <div className="font-medium">{r.actor_name || r.actor_id}</div>
                          <div className="text-slate-500">{r.actor_role}</div>
                        </td>
                        <td className="p-2">
                          {r.target_customer_id ? (
                            <div>
                              <div>{r.target_customer_name || r.target_customer_id}</div>
                              <div className="text-slate-400 text-[10px]">{r.target_customer_id}</div>
                            </div>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className={`p-2 text-right whitespace-nowrap ${
                          r.event_type.includes('debit') ? 'text-red-700' :
                          r.event_type.includes('credit') || r.event_type.includes('topup') ? 'text-emerald-700' : ''
                        }`}>{fmtAmount(r.amount)}</td>
                        <td className="p-2">
                          {r.ip ? (
                            <div>
                              <div className="font-mono">{r.ip}</div>
                              {r.location && (
                                <div className="text-slate-500 flex items-center gap-0.5">
                                  <MapPin className="h-3 w-3" />
                                  {[r.location.city, r.location.country_code].filter(Boolean).join(', ')}
                                </div>
                              )}
                            </div>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

